import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

export const TRANSFORM_E844D_MODIFIER_TAG = "GNTLYRULES" as const;

export const SD_E844D_FIELDS = [
  "record_type",
  "record_count",
  "company",
  "division",
  "file_group",
  "trans_type",
  "trans_sub_type",
  "trading_partner",
  "duns_gl_branch",
  "supplier_sell_location",
  "selling_location",
  "shipping_location",
  "short_ship_location_name",
  "short_sell_location_name",
  "branch_name",
  "customer_name_city",
  "customer_name",
  "customer_addr_1",
  "customer_addr_2",
  "customer_city",
  "customer_state",
  "customer_postal_code",
  "customer_country",
  "rebate_type",
  "supplier_contract_no",
  "agreement_no",
  "sold_to_no",
  "ship_to_no",
  "order_no",
  "backorder_level",
  "backorder_level_100",
  "assigned_id",
  "transfer_type_code",
  "line_assigned_id",
  "vendor_catalogue_qualifier",
  "vendor_catalog_no",
  "upc_qualifier",
  "upc_code",
  "vendor_product_qualifier",
  "vendor_product_no",
  "buyer_product_qualifier",
  "buyer_product_no",
  "uom",
  "unit_price_wholesale",
  "basis_uom_wholesale",
  "unit_price_quoted",
  "basis_uom_quoted",
  "quantity",
  "quantity_uom",
  "claim_amount",
  "invoice_no",
  "order_line_no",
  "order_line_ref",
  "invoice_date",
  "ship_date",
  "order_date",
  "buying_party_currency",
  "selling_party_currency",
  "accept_reject",
  "reject_reason_code",
  "extract_control_no",
  "batch_control_no",
  "buyers_debit_memo",
  "added_manually",
  "batch_create_date",
  "batch_create_time",
  "email_status",
  "sent_date",
  "sent_time",
  "resent_date",
  "resent_time",
  "last_modified_date",
  "last_modified_time",
  "last_modified_by",
  "last_modified_program",
  "outbound_flag",
  "item_with_prefix",
  "claim_number",
  "ship_type",
  "supplier_credit_memo",
  "supplier_batch_id",
  "vendor_no",
  "product_on_fly_flag",
  "edi_supplier_flag",
  "previous_status",
  "landed_cost",
  "last_po_number",
  "last_po_vendor",
  "price_class",
  "price_sub_class",
  "cost_override_flag",
] as const;

export const TRANSFORM_E844D_TARGET_FIELDS = SD_E844D_FIELDS.filter(
  (field) => field !== "claim_amount",
);

const SD_E844D_FIELD_SET = new Set<string>(SD_E844D_FIELDS);
const TRANSFORM_E844D_TARGET_FIELD_SET = new Set<string>(
  TRANSFORM_E844D_TARGET_FIELDS,
);
const FIELD_NAME_PATTERN = /^[a-z][a-z0-9_]*$/;

const OPERATORS_REQUIRING_VALUE = new Set<string>([
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "contains",
  "not_contains",
  "starts_with",
  "ends_with",
  "in",
  "not_in",
  "regex_match",
]);

const sdE844dFieldSchema = z
  .string()
  .regex(
    FIELD_NAME_PATTERN,
    "Field must be snake_case (letters, numbers, underscores)",
  )
  .refine((field) => SD_E844D_FIELD_SET.has(field), {
    message: "Field must be a valid sd_e844d column",
  });

const conditionSchema = z
  .object({
    field: sdE844dFieldSchema.describe(
      "sd_e844d column name in snake_case (e.g., 'claim_number')",
    ),
    operator: z.enum([
      "eq",
      "neq",
      "gt",
      "gte",
      "lt",
      "lte",
      "contains",
      "not_contains",
      "starts_with",
      "ends_with",
      "is_null",
      "is_not_null",
      "in",
      "not_in",
      "regex_match",
    ]),
    value: z.unknown().optional(),
  })
  .superRefine((condition, ctx) => {
    if (
      OPERATORS_REQUIRING_VALUE.has(condition.operator) &&
      condition.value === undefined
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["value"],
        message: `Operator "${condition.operator}" requires a value`,
      });
    }

    if (
      (condition.operator === "in" || condition.operator === "not_in") &&
      condition.value !== undefined &&
      !Array.isArray(condition.value) &&
      typeof condition.value !== "string"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["value"],
        message: `Operator "${condition.operator}" requires an array or comma-separated string`,
      });
    }

    if (
      condition.operator === "regex_match" &&
      condition.value !== undefined &&
      typeof condition.value !== "string"
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["value"],
        message: 'Operator "regex_match" requires a string pattern',
      });
    }
  });

const transformOpSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("copy") }),
  z.object({
    type: z.literal("slice"),
    start: z
      .number()
      .describe("Inclusive start index (negative counts from end)"),
    end: z
      .number()
      .optional()
      .describe(
        "Exclusive end index. Omit to slice to end. Negative counts from end (e.g., -3 drops last 3 chars).",
      ),
  }),
  z.object({
    type: z.literal("regex_extract"),
    pattern: z
      .string()
      .describe("Regex pattern; capture group will be extracted"),
    group: z
      .number()
      .prefault(0)
      .describe("Capture group index (0 = full match, 1 = first group, ...)"),
  }),
  z.object({
    type: z.literal("replace"),
    pattern: z.string().describe("Regex pattern to replace"),
    replacement: z.string().describe("Replacement string ($1, $2 supported)"),
  }),
  z.object({
    type: z.literal("prepend"),
    value: z.string().describe("String to prepend to source value"),
  }),
  z.object({
    type: z.literal("append"),
    value: z.string().describe("String to append to source value"),
  }),
  z.object({
    type: z.literal("literal"),
    value: z.string().describe("Constant value to set on the target field"),
  }),
]);

const transformSchema = z
  .object({
    target: z
      .string()
      .regex(
        FIELD_NAME_PATTERN,
        "Target must be snake_case (letters, numbers, underscores)",
      )
      .refine((field) => TRANSFORM_E844D_TARGET_FIELD_SET.has(field), {
        message:
          "Target must be a writable sd_e844d column (claim_amount is forbidden)",
      })
      .describe(
        "sd_e844d column to write (snake_case, e.g. 'supplier_contract_no')",
      ),
    source: z
      .object({
        field: sdE844dFieldSchema.describe(
          "sd_e844d column to read source value from (snake_case). Required for all op types except 'literal'.",
        ),
      })
      .optional()
      .describe("Source field. Omit only when op.type is 'literal'."),
    op: transformOpSchema,
  })
  .superRefine((transform, ctx) => {
    if (transform.op.type !== "literal" && !transform.source?.field) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["source", "field"],
        message: `Transform op "${transform.op.type}" requires source.field`,
      });
    }
  });

/**
 * Transform E844D Fields Step
 *
 * Evaluates configurable rules against pending sd_e844d rows and applies field
 * transforms when rules match. Rules can match using the same condition engine
 * as classify_master_data (regex, contains, ranges, AND/OR groups, etc.).
 *
 * When at least one rule matches a row:
 *   - All matching rules' transforms are applied (priority order, highest first).
 *   - last_modified_program is stamped with the fixed modifier tag
 *     "GNTLYRULES" so downstream classify rules can gate behavior using
 *     `last_modified_program eq "GNTLYRULES"`.
 *   - last_modified_date / last_modified_time are stamped to current run time.
 *   - A single field_modified_by_rule activity is logged per touched row,
 *     listing every rule that fired and the before/after values of each field.
 *
 * Rows where no rule matches are left unchanged (no stamp, no activity).
 *
 * Use this step BEFORE classify_master_data so classify can react to the
 * transformed values (e.g. a new supplier_contract_no prefix that triggers an
 * "Internal contracts" classification).
 *
 * Note: claim_amount is intentionally NOT a valid transform target. It is
 * derived by OPE84440 from quantity/wholesale/quoted price. Use the pending
 * transactions edit modal if you need to recalculate it.
 */
export const transformE844dFieldsStep = defineStepConfig({
  type: "transform_e844d_fields",
  name: "Transform E844D Fields",
  description:
    "Apply rule-based field modifications to sd_e844d rows. Stamps last_modified_program so classify can detect transformed rows.",
  category: "Transformation",
  inputSchema: z.object({
    updatedAfter: z
      .string()
      .describe(
        "ISO timestamp — only consider rows with updatedAt >= this value. " +
          "Wire from convert step's updatedAfter output. Required to prevent full table scans.",
      ),
    company: z
      .string()
      .optional()
      .describe(
        "Restrict this step to one company code. Wire from the bulk insert " +
          "step's company output so a fanout child touches only its own slice.",
      ),
    division: z
      .string()
      .optional()
      .describe(
        "Restrict this step to one division code. Wire from the bulk insert " +
          "step's division output so a fanout child touches only its own slice.",
      ),
  }),

  outputSchema: z.object({
    startedAt: z
      .string()
      .describe(
        "ISO timestamp captured at the start of this step. Wire to classify's updatedAfter input.",
      ),
    processedCount: z.number().describe("Total rows evaluated"),
    modifiedCount: z.number().describe("Rows that had at least one rule fire"),
    rulesFired: z
      .record(z.string(), z.number())
      .describe("Count of how many rows matched each rule, keyed by rule name"),
  }),

  configSchema: z.object({
    sourceStatusFilter: z
      .array(z.string())
      .prefault(["pending"])
      .describe("Only process rows with these statuses"),
    rules: z
      .array(
        z.object({
          name: z.string().describe("Rule name (audit trail + activity log)"),
          priority: z
            .number()
            .prefault(0)
            .describe(
              "Higher priority rules apply first. All matching rules apply (unlike classify which stops at first match).",
            ),
          conditions: z
            .array(conditionSchema)
            .prefault([])
            .describe(
              "Flat conditions (AND logic). Used when conditionGroups is empty.",
            ),
          conditionGroups: z
            .array(
              z.object({
                logic: z.enum(["AND", "OR"]),
                conditions: z.array(conditionSchema),
              }),
            )
            .optional()
            .describe(
              "Condition groups with AND/OR logic. Takes precedence over flat conditions.",
            ),
          conditionGroupsLogic: z
            .enum(["AND", "OR"])
            .prefault("OR")
            .describe(
              "How condition groups combine (OR = any group, AND = all groups).",
            ),
          transforms: z
            .array(transformSchema)
            .min(1)
            .describe("Field transforms applied when this rule matches"),
        }),
      )
      .prefault([])
      .describe(
        "Transformation rules. All matching rules apply per row, highest priority first.",
      ),
    batchSize: z
      .number()
      .min(1)
      .prefault(500)
      .describe("Rows per DB transaction"),
    limit: z
      .number()
      .min(0)
      .prefault(0)
      .describe("Max rows to process per run (0 = unlimited)"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },
});
